import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  User,
  Phone,
  Calendar,
  Edit,
  Activity,
  Heart,
  AlertCircle,
  Pill,
  LayoutDashboard,
  Target
} from 'lucide-react';
import PageHeader from '../components/PageHeader';
import { useAuth } from '../contexts/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Traduceri în română pentru obiectivele de sănătate (GoalType enum)
const HEALTH_GOAL_LABELS: Record<string, string> = {
  // HEALTHY MODE
  WEIGHT_LOSS: 'Vreau să slăbesc',
  MUSCLE_GAIN: 'Cresc masa musculară',
  HEALTHY_LIFESTYLE: 'Stil de viață sănătos',
  REDUCE_BELLY_FAT: 'Reduc grăsimea abdominală',
  INCREASE_ENERGY: 'Mai multă energie zilnic',
  CLEAN_EATING: 'Mănânc mai curat și natural',
  REDUCE_SUGAR_CARBS: 'Reduc zahărul și carbohidrații',
  BETTER_SLEEP: 'Dorm mai bine',
  REDUCE_BLOATING: 'Scap de balonare',
  START_EXERCISE: 'Încep să fac sport',
  IMPROVE_FOCUS: 'Îmbunătățesc concentrarea',
  MAINTAIN_WEIGHT: 'Mențin greutatea',
  HEALTHY_WEIGHT_GAIN: 'Cresc în greutate sănătos',
  // CHRONIC MODE
  EASY_STANDING: 'Mă ridic ușor de pe scaun',
  REDUCE_INFLAMMATION: 'Reduc inflamația',
  RECOVER_MUSCLE_STRENGTH: 'Recuperez forța musculară',
  CLIMB_STAIRS: 'Urc scările fără durere',
  REDUCE_JOINT_PAIN: 'Reduc durerile articulare',
  BREATHE_BETTER: 'Respir mai bine',
  STABILIZE_GLUCOSE: 'Stabilizez glicemia',
  IMPROVE_BALANCE: 'Îmbunătățesc echilibrul',
  REDUCE_CHRONIC_FATIGUE: 'Reduc oboseala cronică',
  IMPROVE_DIGESTION: 'Îmbunătățesc digestia',
  REGAIN_MOBILITY: 'Recapăt mobilitatea',
  PREVENT_DISEASE_WORSENING: 'Previn agravarea bolii',
  HEALTHY_WEIGHT_LOSS_CHRONIC: 'Slăbesc sănătos',
  CONTROL_CHRONIC_PAIN: 'Controlez durerile cronice',
  IMPROVE_LIFE_WITH_DISEASE: 'Îmbunătățesc viața cu boala',
};

const getHealthGoalLabel = (goal: string): string => {
  return HEALTH_GOAL_LABELS[goal] || goal.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, (l) => l.toUpperCase());
};

interface MedicalProfile {
  id: string;
  phone?: string;
  birthDate?: string;
  gender?: string;
  weight?: number;
  height?: number;
  bmi?: number;
  primaryCondition?: string;
  secondaryConditions?: string[];
  diagnosisDate?: string;
  doctorName?: string;
  diseaseStage?: string;
  symptoms?: string[];
  foodIntolerances?: string[];
  mobilityLevel?: string;
  activityLevel?: string;
  emergencyContact?: {
    name: string;
    phone: string;
    relation: string;
  };
  completionPercent: number;
}

export default function ProfileView() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [profile, setProfile] = useState<MedicalProfile | null>(null);
  const [loading, setLoading] = useState(true);

  // FII FIT = aplicație 100% fitness. Cardul "Medicamente & Tratamente"
  // este ascuns indiferent de ce ar fi salvat user-ul în DB.
  const isChronicMode = false;
  void user;

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_URL}/api/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProfile(response.data);
    } catch (error) {
      console.error('Error loading profile:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 flex items-center justify-center">
        <div className="w-16 h-16 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50">
      <PageHeader
        title="Profilul Meu Fitness"
        subtitle="Informațiile tale de fitness"
        showBack={true}
        backTo="/dashboard"
        icon={<User className="w-5 h-5" />}
        actions={
          <button
            onClick={() => navigate('/profile/edit')}
            className="flex items-center px-3 py-2 sm:px-6 sm:py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm sm:text-base"
          >
            <Edit className="w-4 h-4 sm:w-5 sm:h-5 sm:mr-2" />
            <span className="hidden sm:inline">Editează</span>
          </button>
        }
      />

      <div className="max-w-4xl mx-auto px-4 py-6">
        {/* Completion Progress */}
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Profil completat</span>
            <span className="text-sm font-medium text-blue-600">{profile?.completionPercent || 0}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="bg-blue-600 h-2 rounded-full transition-all"
              style={{ width: `${profile?.completionPercent || 0}%` }}
            />
          </div>
        </div>

        {/* Date Personale */}
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-center mb-4">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
              <User className="w-6 h-6 text-blue-600" />
            </div>
            <h2 className="text-xl font-bold text-gray-900">Date Personale</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {profile?.phone && (
              <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                <Phone className="w-5 h-5 text-gray-600 mr-3" />
                <div>
                  <p className="text-xs text-gray-500">Telefon</p>
                  <p className="text-sm font-medium text-gray-900">{profile.phone}</p>
                </div>
              </div>
            )}

            {profile?.birthDate && (
              <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                <Calendar className="w-5 h-5 text-gray-600 mr-3" />
                <div>
                  <p className="text-xs text-gray-500">Data nașterii</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(profile.birthDate).toLocaleDateString('ro-RO')}
                  </p>
                </div>
              </div>
            )}

            {profile?.gender && (
              <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                <User className="w-5 h-5 text-gray-600 mr-3" />
                <div>
                  <p className="text-xs text-gray-500">Gen</p>
                  <p className="text-sm font-medium text-gray-900 capitalize">{profile.gender}</p>
                </div>
              </div>
            )}

            {profile?.weight && (
              <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                <Activity className="w-5 h-5 text-gray-600 mr-3" />
                <div>
                  <p className="text-xs text-gray-500">Greutate</p>
                  <p className="text-sm font-medium text-gray-900">{profile.weight} kg</p>
                </div>
              </div>
            )}

            {profile?.height && (
              <div className="flex items-center p-3 bg-gray-50 rounded-lg">
                <Activity className="w-5 h-5 text-gray-600 mr-3" />
                <div>
                  <p className="text-xs text-gray-500">Înălțime</p>
                  <p className="text-sm font-medium text-gray-900">{profile.height} cm</p>
                </div>
              </div>
            )}

            {profile?.bmi && (
              <div className="flex items-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                <Heart className="w-5 h-5 text-blue-600 mr-3" />
                <div>
                  <p className="text-xs text-blue-700">BMI</p>
                  <p className="text-sm font-medium text-blue-900">{profile.bmi}</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Obiective de Sănătate */}
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                <Target className="w-6 h-6 text-blue-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Obiective de Sănătate</h2>
            </div>
            <button
              onClick={() => navigate('/health-goal')}
              className="flex items-center gap-1 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
            >
              <Edit className="w-4 h-4" />
              <span>Schimbă</span>
            </button>
          </div>

          {user?.healthGoals && Array.isArray(user.healthGoals) && user.healthGoals.length > 0 ? (
            <div className="flex flex-wrap gap-2">
              {user.healthGoals.map((goal: string, idx: number) => (
                <span
                  key={idx}
                  className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm font-medium"
                >
                  🎯 {getHealthGoalLabel(goal)}
                </span>
              ))}
            </div>
          ) : (
            <div className="text-center py-4">
              <p className="text-gray-500 text-sm mb-3">Nu ai selectat niciun obiectiv de sănătate</p>
              <button
                onClick={() => navigate('/health-goal')}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium transition-colors"
              >
                Selectează obiective →
              </button>
            </div>
          )}
        </div>

        {/* Obiective Calorice */}
        <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 mb-4 sm:mb-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mr-3">
                <Target className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-gray-900">Obiective Calorice</h2>
                <p className="text-xs text-gray-500">Setează sau modifică obiectivele tale calorice zilnice</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/calorie-goals-setup')}
              className="flex items-center gap-1 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm font-medium transition-colors"
            >
              <Edit className="w-4 h-4" />
              <span>Configurează →</span>
            </button>
          </div>
        </div>


        {/* Medicamente - Only for CHRONIC mode */}
        {isChronicMode && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                  <Pill className="w-6 h-6 text-purple-600" />
                </div>
                <h2 className="text-xl font-bold text-gray-900">Medicamente & Tratamente</h2>
              </div>
              <button
                onClick={() => navigate('/profile/medications')}
                className="flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Gestionează
              </button>
            </div>
            <p className="text-sm text-gray-600">
              Accesează lista completă de medicamente și tratamente curente
            </p>
          </div>
        )}

        {/* Intoleranțe Alimentare */}
        {profile?.foodIntolerances && profile.foodIntolerances.length > 0 && (
          <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mr-3">
                <AlertCircle className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Intoleranțe Alimentare</h2>
            </div>

            <div className="flex flex-wrap gap-2">
              {profile.foodIntolerances.map((intolerance, idx) => (
                <span
                  key={idx}
                  className="px-3 py-1 bg-red-100 text-red-800 rounded-full text-sm capitalize"
                >
                  {intolerance.replace(/-/g, ' ')}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Contact Urgență */}
        {profile?.emergencyContact && (
          <div className="bg-white rounded-2xl shadow-xl p-6">
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center mr-3">
                <Phone className="w-6 h-6 text-purple-600" />
              </div>
              <h2 className="text-xl font-bold text-gray-900">Contact de Urgență</h2>
            </div>

            <div className="space-y-3">
              <div>
                <p className="text-sm text-gray-500">Nume</p>
                <p className="text-base font-medium text-gray-900">{profile.emergencyContact.name}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Telefon</p>
                <p className="text-base font-medium text-gray-900">{profile.emergencyContact.phone}</p>
              </div>
              <div>
                <p className="text-sm text-gray-500">Relație</p>
                <p className="text-base font-medium text-gray-900 capitalize">{profile.emergencyContact.relation}</p>
              </div>
            </div>
          </div>
        )}

        {/* Buton Dashboard - pentru navigare rapidă */}
        <div className="mt-8 mb-8">
          <button
            onClick={() => navigate('/dashboard')}
            className="w-full flex items-center justify-center gap-3 px-6 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-2xl shadow-lg hover:from-blue-700 hover:to-blue-800 transition-all active:scale-[0.98]"
          >
            <LayoutDashboard className="w-6 h-6" />
            <span className="text-lg font-semibold">Înapoi la Dashboard</span>
          </button>
        </div>
      </div>
    </div>
  );
}
